import React, { useState } from 'react';
import { 
  Typography, 
  Box,
  Divider,
  Skeleton,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { RevenueChart } from './Charts/RevenueChart';
import { CategoryPieChart } from './Charts/CategoryPieChart';
import InsightCard from './shared/InsightCard';

interface SalesAnalysisCardProps {
  data: {
    totalRevenue: number;
    averagePerVisit: number;
    lastThreeMonthsAverage: number;
    lifetimeValue: number;
  } | null;
  loading?: boolean;
  treatmentRecords?: Array<{
    id: string;
    treatmentDate: string;
    totalAmount: number;
    treatmentMenu: string;
  }>;
}

const SalesAnalysisCard: React.FC<SalesAnalysisCardProps> = ({ data, loading = false, treatmentRecords = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [chartType, setChartType] = useState<'bar' | 'pie'>('bar');




  if (loading) {
    return (
      <InsightCard
        title="売上・LTV分析"
        cardVariant="primary"
      >
        <Skeleton variant="text" width="70%" height={40} />
        <Skeleton variant="text" width="50%" height={24} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="60%" height={24} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="55%" height={24} sx={{ mt: 1 }} />
      </InsightCard>
    );
  }

  if (!data) {
    return (
      <InsightCard
        title="売上・LTV分析"
        cardVariant="primary"
      >
        <Typography color="text.secondary">
          データがありません
        </Typography>
      </InsightCard>
    );
  }

  const percentageChange = data.averagePerVisit > 0 
    ? Math.round(((data.lastThreeMonthsAverage - data.averagePerVisit) / data.averagePerVisit) * 100)
    : 0;

  return (
    <InsightCard
      title="売上・LTV分析"
      cardVariant="primary"
    >

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AttachMoneyIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                累計売上額
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              <CountUp 
                end={data.totalRevenue} 
                duration={2} 
                separator="," 
                prefix="¥" 
              />
            </Typography>
          </Box>
        </motion.div>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'grid', gap: 2 }}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ReceiptIcon sx={{ color: '#2196F3', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  平均客単価
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                <CountUp 
                  end={data.averagePerVisit} 
                  duration={1.5} 
                  separator="," 
                  prefix="¥" 
                />
              </Typography>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  直近3ヶ月平均
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  <CountUp 
                    end={data.lastThreeMonthsAverage} 
                    duration={1.5} 
                    separator="," 
                    prefix="¥" 
                  />
                </Typography>
                {percentageChange !== 0 && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: percentageChange > 0 ? 'success.main' : 'error.main',
                        fontWeight: 'bold'
                      }}
                    >
                      {percentageChange > 0 ? '+' : ''}{percentageChange}%
                    </Typography>
                  </motion.div>
                )}
              </Box>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <AccountBalanceWalletIcon sx={{ color: 'primary.main', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  顧客生涯価値（LTV）
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                <CountUp 
                  end={data.lifetimeValue} 
                  duration={2} 
                  separator="," 
                  prefix="¥" 
                />
              </Typography>
            </Box>
          </motion.div>
        </Box>

        {/* グラフ展開ボタン */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{ 
              bgcolor: 'primary.50',
              '&:hover': { bgcolor: 'primary.100' }
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ShowChartIcon />}
          </IconButton>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            {expanded ? 'グラフを閉じる' : 'グラフを表示'}
          </Typography>
        </Box>

        {/* グラフエリア */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            
            {/* グラフタイプ切り替え */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                売上分析
              </Typography>
              <ToggleButtonGroup
                value={chartType}
                exclusive
                onChange={(_, newType) => newType && setChartType(newType)}
                size="small"
              >
                <ToggleButton value="bar">
                  <BarChartIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="pie">
                  <PieChartIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {chartType === 'bar' ? (
              <RevenueChart treatmentRecords={treatmentRecords} />
            ) : (
              <CategoryPieChart treatmentRecords={treatmentRecords} />
            )}
          </Box>
        </Collapse>
    </InsightCard>
  );
};

export default SalesAnalysisCard;